/**
 * Shared list-view table column spec so <thead> in Section and <tbody> cells in CardListView stay aligned.
 */

/** API may pass an array of key names or a flattened map keyed by id (show-page state). */
function listViewCustomFieldColumnNames(customFieldKeys) {
  if (!customFieldKeys) return [];
  if (Array.isArray(customFieldKeys)) {
    return customFieldKeys.filter((k) => k != null && k !== '');
  }
  if (typeof customFieldKeys === 'object') {
    return Object.values(customFieldKeys)
      .map((entry) => (typeof entry === 'string' ? entry : entry?.customFieldKey?.name))
      .filter((name) => name != null && name !== '');
  }
  return [];
}

function customFieldValuePresent(value) {
  if (value == null) return false;
  return String(value).trim() !== '';
}

/** True if this asset has at least one non-empty value for the given custom field key name. */
function assetHasCustomFieldDataForKey(attributes, keyName) {
  const prioritized = attributes.prioritized_custom_fields;
  if (!prioritized || !Array.isArray(prioritized)) return false;
  const entry = prioritized.find((kv) => kv.key?.name === keyName);
  if (!entry?.values?.length) return false;
  return entry.values.some(({ value }) => customFieldValuePresent(value));
}

/**
 * Ordered custom field column names that actually have data on at least one asset
 * in the current section (typically the loaded page of assets).
 */
export function customFieldKeysWithDataInSection(sectionAssets, orderedKeyNames) {
  if (!orderedKeyNames?.length || !sectionAssets?.length) return [];
  return orderedKeyNames.filter((keyName) => (
    sectionAssets.some(({ attributes }) => assetHasCustomFieldDataForKey(attributes, keyName))
  ));
}

/**
 * When ordered keys from the server are missing/empty, derive prioritized key names that have
 * data on at least one asset in the section (preserves first-seen order from section assets).
 */
function prioritizedKeyNamesWithDataInSection(sectionAssets) {
  if (!sectionAssets?.length) return [];
  const ordered = [];
  const seen = new Set();

  sectionAssets.forEach(({ attributes }) => {
    const prioritized = attributes?.prioritized_custom_fields;
    if (!Array.isArray(prioritized)) return;
    prioritized.forEach((kv) => {
      const name = kv?.key?.name;
      if (!name || seen.has(name)) return;
      if (assetHasCustomFieldDataForKey(attributes, name)) {
        seen.add(name);
        ordered.push(name);
      }
    });
  });
  return ordered;
}

/** Column keys for list/grid when custom fields are shown: configured order, minus keys with no section data. */
export function sectionCustomFieldColumnKeys(showCustomFieldsSection, customFieldKeys, sectionAssets) {
  if (!showCustomFieldsSection) return [];
  const ordered = listViewCustomFieldColumnNames(customFieldKeys);
  let keys = customFieldKeysWithDataInSection(sectionAssets, ordered);
  if (!keys.length) {
    keys = prioritizedKeyNamesWithDataInSection(sectionAssets);
  }
  return keys;
}

export function buildListViewTableSpec({
  disableSort,
  showCustomFieldsSection,
  showTags,
  customFieldKeys,
}) {
  const customFieldKeysList = showCustomFieldsSection ? listViewCustomFieldColumnNames(customFieldKeys) : [];
  const hasCustomFieldColumns = customFieldKeysList.length > 0;

  return {
    showReorder: !disableSort,
    customFieldKeys: customFieldKeysList,
    // List view: hide Tags when custom fields are enabled for the view (custom fields replace that column).
    showTagsColumn: showTags && !showCustomFieldsSection,
    showTagsPlaceholder: !showTags && !hasCustomFieldColumns && !showCustomFieldsSection,
    showViewOnlyColumn: BFG.hasFeature('view_only_assets'),
  };
}

export function countListViewTableColumns(spec) {
  let n = 0;
  if (spec.showReorder) n += 1;
  n += 2; // preview + select
  n += 1; // name
  n += 1; // status / badge
  n += 1; // file type / attachment
  if (spec.showViewOnlyColumn) n += 1;
  const cfCount = spec.customFieldKeys?.length ?? 0;
  n += cfCount;
  if (spec.showTagsColumn) {
    n += 1;
  } else if (spec.showTagsPlaceholder) {
    n += 1;
  }
  n += 1; // actions
  return n;
}
